import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { portalSession, StoredPatientContext } from './portalSession';
import { requestSession } from './requestSession';

// Custom-auth client for the healthcom-schedule patient portal.
// Replaces the Amplify-based Cognito flow. All four endpoints are
// public (the wrapper accepts no scope); auth is done by exchanging
// short-lived OTP challenges for long-lived session tokens.

export type AuthMatch = {
  patientId: string;
  fname: string;
  lname: string;
  clinicId: number;
  clinicName: string;
};

const NOAUTH = 'api-noauth';

export type StartOtpResult = {
  // Absent when the backend short-circuited the no-account branch:
  // login-only callers see `{ hasMatchingAccount: false }` with no
  // challenge minted, so any code field is irrelevant.
  challengeId?: string;
  expiresAt?: number;
  message: string;
  // When `throttled` is true, no SMS / email was actually dispatched
  // and the caller should show a "please wait Xs" message instead of
  // pretending a new code is on the way. `retryAfterSeconds` is the
  // recommended countdown before the next start request.
  throttled?: boolean;
  retryAfterSeconds?: number;
  // Whether a Patient row matched the contact at start time. Login
  // surfaces pass `requireAccount: true` and branch on this to show
  // an honest "no account" message instead of advancing to the OTP
  // step; booking surfaces ignore it (they advance either way and
  // mint a pending Patient at booking time).
  hasMatchingAccount?: boolean;
};

const startOtp = async (input: {
  contact: string;
  contactType: 'email' | 'phone';
  // Login-only surfaces opt in by setting this to `true`. The backend
  // then refuses to mint a code for a contact with no matching
  // Patient row, returning `hasMatchingAccount: false` so the caller
  // can keep the user on the identify step with a clear message.
  requireAccount?: boolean;
}): Promise<StartOtpResult> => {
  return ChiroUpAPI.post(NOAUTH, '/auth/otp/start', { body: input });
};

const verifyOtp = async (input: {
  challengeId: string;
  code: string;
}): Promise<{ challengeId: string; matches: AuthMatch[] }> => {
  return ChiroUpAPI.post(NOAUTH, '/auth/otp/verify', { body: input });
};

const startSession = async (input: {
  challengeId: string;
  patientId: string;
  clinicId: number;
}): Promise<{
  token: string;
  expiresAt: string;
  patient: StoredPatientContext;
}> => {
  const res = await ChiroUpAPI.post(NOAUTH, '/auth/session/start', { body: input });

  if (res?.token) {
    portalSession.setToken(res.token, res.expiresAt);
  }

  if (res?.patient) {
    portalSession.setPatient(res.patient);
  }

  return res;
};

// Legacy username/password login. Authenticates against the original
// healthcom-app Cognito patient pool server-side. On the happy path
// returns a patient-scoped portal session bound to the row matched
// by the legacy username. When Cognito needs a follow-up
// (NEW_PASSWORD_REQUIRED — admin-created users that haven't set a
// password yet) the response carries the Cognito Session token; the
// caller passes it back to `legacyNewPassword` along with the
// patient's chosen new password to finish the flow.
type LegacyLoginSuccess = {
  token: string;
  expiresAt: string;
  patient: StoredPatientContext;
};

type LegacyLoginNewPasswordRequired = {
  challenge: 'NEW_PASSWORD_REQUIRED';
  session: string;
  username: string;
};

export type LegacyLoginResult = LegacyLoginSuccess | LegacyLoginNewPasswordRequired;

export const isNewPasswordRequired = (
  res: LegacyLoginResult
): res is LegacyLoginNewPasswordRequired =>
  (res as LegacyLoginNewPasswordRequired)?.challenge === 'NEW_PASSWORD_REQUIRED' &&
  typeof (res as LegacyLoginNewPasswordRequired)?.session === 'string';

const legacyLogin = async (input: {
  username: string;
  password: string;
}): Promise<LegacyLoginResult> => {
  const res = await ChiroUpAPI.post(NOAUTH, '/auth/legacy/login', { body: input });

  if (isNewPasswordRequired(res)) return res;

  if (res?.token) {
    portalSession.setToken(res.token, res.expiresAt);
  }

  if (res?.patient) {
    portalSession.setPatient(res.patient);
  }

  return res;
};

const legacyNewPassword = async (input: {
  username: string;
  newPassword: string;
  session: string;
}): Promise<LegacyLoginSuccess> => {
  const res = await ChiroUpAPI.post(NOAUTH, '/auth/legacy/login/new-password', {
    body: input,
  });

  if (res?.token) {
    portalSession.setToken(res.token, res.expiresAt);
  }

  if (res?.patient) {
    portalSession.setPatient(res.patient);
  }

  return res;
};

// Re-bind the existing patient session to a different clinic without
// minting a new token. Server confirms the target clinic hosts a
// Patient row sharing the session's verified email/phone before
// updating the row; no OTP challenge involved.
const switchClinic = async (input: {
  clinicId: number;
}): Promise<{ patient: StoredPatientContext }> => {
  const res = await ChiroUpAPI.post('api', '/me/switch-clinic', { body: input });

  if (res?.patient) {
    portalSession.setPatient(res.patient);
  }

  return res;
};

const logout = async (): Promise<void> => {
  // Capture the in-flight session before we touch anything — the
  // server-side revoke must use *this* token even if a request-flow
  // token is also stored. Clear local state up front so a network
  // failure on the revoke can't leave the client in a half-signed-in
  // state with a fresh request token bleeding through.
  const portalToken = portalSession.getToken();

  portalSession.clear();
  requestSession.clear();

  if (!portalToken) return;

  try {
    await fetch(`${import.meta.env.VITE_HEALTHCOM_SCHEDULE_API as string}/auth/session/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${portalToken}`,
      },
      body: '{}',
    });
  } catch {
    /* server-side revoke failure is fine — local state is already
       cleared so subsequent requests will look unauthenticated. */
  }
};

export const authService = {
  startOtp,
  verifyOtp,
  startSession,
  switchClinic,
  legacyLogin,
  legacyNewPassword,
  logout,
};
